import type { ConnectionIntent } from './user.js';

/**
 * Connection-request system (supersedes the earlier "introductions").
 *
 * Stored status is one of six values; the client derives the direction-aware
 * UI state (outgoing_pending vs incoming_pending) from requesterUid.
 * A conversation exists ONLY after status 'accepted' — enforced server-side.
 */
export type ConnectionStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'withdrawn'
  | 'blocked';

/** Direction-aware state used by the UI. */
export type ConnectionUiState =
  | 'none'
  | 'outgoing_pending'
  | 'incoming_pending'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'withdrawn'
  | 'blocked';

export interface ConnectionDoc {
  id: string;
  /** Sorted "uidA_uidB" for pair queries and duplicate prevention. */
  pairId: string;
  requesterUid: string;
  recipientUid: string;
  status: ConnectionStatus;
  introMessage?: string;
  createdAtISO: string;
  respondedAtISO?: string;
  /** Pending requests expire (config connection.pendingExpiryDays). */
  expiresAtISO: string;
  conversationId?: string;
}

export function connectionUiState(doc: ConnectionDoc | null, viewerUid: string): ConnectionUiState {
  if (!doc) return 'none';
  if (doc.status === 'pending') {
    return doc.requesterUid === viewerUid ? 'outgoing_pending' : 'incoming_pending';
  }
  return doc.status;
}

// ---------------------------------------------------------------------------
// Compatibility (deterministic, cached, versioned — Claude only EXPLAINS it)
// ---------------------------------------------------------------------------

/**
 * `kundli` is the HEADLINE dimension, not one signal among seven.
 *
 * It used to be buried at a 0.25 share inside `longTerm`, which meant the
 * guna count an Indian user actually came for could move the overall score by
 * about three points. It is now its own top-level dimension carrying the
 * dominant weight for romance and a leading weight everywhere else — see
 * COMPATIBILITY_WEIGHTS in packages/matching.
 *
 * It is listed FIRST because every consumer that renders the breakdown walks
 * COMPATIBILITY_DIMENSIONS in order.
 */
export type CompatibilityDimensionId =
  | 'kundli'
  | 'emotional'
  | 'communication'
  | 'chemistry'
  | 'stability'
  | 'longTerm'
  | 'friendship'
  | 'professional';

export const COMPATIBILITY_DIMENSIONS: CompatibilityDimensionId[] = [
  'kundli',
  'emotional',
  'communication',
  'chemistry',
  'stability',
  'longTerm',
  'friendship',
  'professional',
];

/**
 * The kundli match in ITS OWN units, for display — 28/36 guna or 7/10
 * poruthams, never a percentage. A user who knows the tradition reads the raw
 * count; rescaling it to 0-100 would throw away the one number they recognise.
 *
 * `system` is chosen by the VIEWER's `matchingRegion`, so the same pair can
 * legitimately carry a different badge for each side. `max` is redundant with
 * `system` and is carried anyway so a client can render "score/max" without
 * knowing either tradition.
 *
 * Absent/null when either side's birth data cannot produce a Moon nakshatra —
 * a missing kundli is shown as missing, never as a low score.
 */
export interface KundliScore {
  system: 'ashtakoota' | 'porutham';
  /** Raw total in the system's own scale: 0-36 ashtakoota, 0-10 porutham. */
  score: number;
  max: 36 | 10;
}

export interface CompatibilityDoc {
  pairId: string;
  userA: string;
  userB: string;
  /** All scores are integers 0–100. */
  overall: number;
  dimensions: Record<CompatibilityDimensionId, number>;
  /** Deterministic factor strings the scores were built from. */
  structuredFactors: string[];
  /** Top strengths ("why you may work well"). */
  highlights: string[];
  /** Top tensions ("potential friction"). */
  frictions: string[];
  /**
   * The kundli match in its own units, or null when birth data is incomplete.
   * Optional only because documents written before this field existed do not
   * carry it; the compatibilityVersion bump retires those on first read.
   */
  kundli?: KundliScore | null;
  scoreVersion: string;
  calculatedAtISO: string;
  /** Cache key — recompute when either profile's inputs change. */
  inputsHash: string;
}

// ---------------------------------------------------------------------------
// AI credit wallet — append-only ledger is canonical; balance is a mirror.
// ---------------------------------------------------------------------------

export type CreditSource =
  | 'introductory'
  | 'purchase'
  | 'promotional'
  | 'admin'
  | 'refund'
  | 'reversal'
  | 'deeper_answer'
  | 'deeper_report'
  | 'master_reading'
  | 'regeneration';

export interface CreditLedgerEntry {
  id: string;
  uid: string;
  type: 'credit' | 'debit';
  source: CreditSource;
  /** Always positive; type carries the sign. */
  amount: number;
  balanceAfter: number;
  /** Generation / purchase / report id this entry belongs to. */
  referenceId?: string;
  /** Prevents double charging on retries. */
  idempotencyKey?: string;
  createdAtISO: string;
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

export interface DiscoveryCard {
  uid: string;
  displayName: string;
  age?: number;
  city?: string;
  /**
   * Storage path of the profile picture, denormalised off `UserDoc.avatar`.
   *
   * The PATH only, not the whole MediaRef: a card avatar is drawn into a fixed
   * circle, so there is no layout to reserve and no blur to render (the
   * initials stand in until the image arrives). Every extra field here is paid
   * for on every card of every feed, cached and re-served.
   *
   * Absent on accounts that joined before the picture became a requirement.
   * Those cards fall back to initials and rank below every card that has one —
   * see discoveryFns.
   */
  avatarPath?: string;
  bio?: string;
  primaryIntention?: ConnectionIntent;
  interests: string[];
  compatibility: {
    overall: number;
    /** One or two strongest dimensions, e.g. "emotional 91". */
    highlights: string[];
  };
  /**
   * The guna/porutham badge, in the viewer's own tradition. Absent when either
   * side has no usable Moon nakshatra — the deck must then show no badge at
   * all rather than a zero, which reads as a bad match instead of no data.
   */
  kundliScore?: KundliScore;
  verified: boolean;
  /** Coarse activity signal, never an exact timestamp. */
  activeBand: 'today' | 'this-week' | 'earlier';
}

export interface DiscoveryFeedResult {
  cards: DiscoveryCard[];
  /** Remaining outgoing requests for the viewer (base lifetime / pro daily). */
  remainingRequests: number;
  requestQuotaKind: 'base-lifetime' | 'pro-daily';
  /**
   * True when the viewer has `matchFilters` set but is not on Pro, so this
   * feed was built WITHOUT them. The UI owes the user that sentence — silently
   * ignoring a filter somebody deliberately set looks like a broken filter,
   * and "your filters are off because they need Pro" is both the truth and the
   * upgrade prompt. Absent/false means the feed honoured whatever was set.
   */
  filtersRequirePro?: boolean;
}
